import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Progress, 
  Table, 
  Divider, 
  Typography, 
  Spin,
  Empty,
  Alert,
  Space,
  Button,
  DatePicker
} from 'antd';
import { 
  UserOutlined, 
  BookOutlined, 
  FileDoneOutlined, 
  TeamOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
  BarChartOutlined,
  ClearOutlined
} from '@ant-design/icons';
import axios from 'axios';
import constants from '../../constants';
import moment from 'moment';

const { Title, Text } = Typography;

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const { RangePicker } = DatePicker;
  // student data
  const currentYear = moment().year();
  const setSessionMonth = (monthsAgo) => {
    const end = moment().subtract(monthsAgo, 'months').endOf('month');
    const start = moment().subtract(monthsAgo, 'months').startOf('month');
    
    setDateRange([start, end]);
    fetchAnalytics([start, end]);
  };

  const handleDateRangeChange = async (dates, dateStrings) => {
    setDateRange(dates);
    fetchAnalytics(dateStrings)  
  };

  const handleClearRange = () => {
    setDateRange(null);
    fetchAnalytics(undefined)  
  };

  const fetchAnalytics = async (date) => {
   setLoading(true)
    try {
      let url
      if(date){
        let sd = new Date(date && date[0]|| new Date());
      sd.setHours(0, 0, 0, 0);
      let ed = new Date(date &&date[1] || new Date());
      ed.setDate(ed.getDate() + 1);
      ed.setHours(0, 0, 0, 0);
      let sdISO = sd.toISOString();
      let edISO = ed.toISOString();
      url=`${constants.NEW_DOMAIN}/api/v1/admin/getAnalyticsByIdForMonth?startDate=${sd}&endDate=${ed}`
      }else{
     url = `${constants.NEW_DOMAIN}/api/v1/admin/getAnalyticsByIdForMonth`
      }
      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_JWT_TOKEN}`
        }
      })
      setAnalytics(res.data.data);
      setLoading(false);
      
    } catch (error) {
      setLoading(false);
      console.log(error)
    }
  }

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Training completion data for the table
  const trainingCompletionData = analytics ? [
    { key: '1', range: '0-25%', count: analytics.trainingCompletion['0-25'], percentage: ((analytics.trainingCompletion['0-25'] / analytics.totalUsers) * 100).toFixed(1) },
    { key: '2', range: '25-50%', count: analytics.trainingCompletion['25-50'], percentage: ((analytics.trainingCompletion['25-50'] / analytics.totalUsers) * 100).toFixed(1) },
    { key: '3', range: '50-75%', count: analytics.trainingCompletion['50-75'], percentage: ((analytics.trainingCompletion['50-75'] / analytics.totalUsers) * 100).toFixed(1) },
    { key: '4', range: '75-100%', count: analytics.trainingCompletion['75-above'], percentage: ((analytics.trainingCompletion['75-above'] / analytics.totalUsers) * 100).toFixed(1) },
  ] : [];

  const trainingColumns = [
    { title: 'Completion Range', dataIndex: 'range', key: 'range' },
    { title: 'Number of Users', dataIndex: 'count', key: 'count' },
    // { 
    //   title: 'Percentage', 
    //   dataIndex: 'percentage', 
    //   key: 'percentage',
    //   render: (text) => `${text}%`
    // },
  ];

  // Interview scores data for the table
  const interviewScoresData = analytics ? [
    { key: '1', range: '0-5', count: analytics.interviewScores['0-5'], percentage: analytics.interviewCompleted > 0 ? ((analytics.interviewScores['0-5'] / analytics.interviewCompleted) * 100).toFixed(1) : 0 },
    { key: '2', range: '5-10', count: analytics.interviewScores['5-10'], percentage: analytics.interviewCompleted > 0 ? ((analytics.interviewScores['5-10'] / analytics.interviewCompleted) * 100).toFixed(1) : 0 },
  ] : [];

  const interviewColumns = [
    { title: 'Score Range', dataIndex: 'range', key: 'range' },
    { title: 'Number of Users', dataIndex: 'count', key: 'count' },
    { 
      title: 'Percentage', 
      dataIndex: 'percentage', 
      key: 'percentage',
      render: (text) => `${text}%`
    },
  ];

  // Internship task submissions data for the table
  const internshipTasksData = analytics ? [
    { key: '1', task: 'Task 1', count: analytics.internshipTaskSubmissions.task1, percentage: analytics.usersStartedInternship > 0 ? ((analytics.internshipTaskSubmissions.task1 / analytics.usersStartedInternship) * 100).toFixed(1) : 0 },
    { key: '2', task: 'Task 2', count: analytics.internshipTaskSubmissions.task2, percentage: analytics.usersStartedInternship > 0 ? ((analytics.internshipTaskSubmissions.task2 / analytics.usersStartedInternship) * 100).toFixed(1) : 0 },
    { key: '3', task: 'Task 3', count: analytics.internshipTaskSubmissions.task3, percentage: analytics.usersStartedInternship > 0 ? ((analytics.internshipTaskSubmissions.task3 / analytics.usersStartedInternship) * 100).toFixed(1) : 0 },
    { key: '4', task: 'Task 4', count: analytics.internshipTaskSubmissions.task4, percentage: analytics.usersStartedInternship > 0 ? ((analytics.internshipTaskSubmissions.task4 / analytics.usersStartedInternship) * 100).toFixed(1) : 0 },
  ] : [];

  const internshipColumns = [
    { title: 'Task', dataIndex: 'task', key: 'task' },
    { title: 'Submissions', dataIndex: 'count', key: 'count' },
    { 
      title: 'Percentage of Interns', 
      dataIndex: 'percentage', 
      key: 'percentage',
      render: (text) => `${text}%`
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '20px' }}>Loading analytics data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  if (!analytics) {
    return <Empty description="No analytics data available" />;
  }

  return (
    <div style={{ padding: '20px' }}>
      <Title level={2}>Program Analytics Dashboard</Title>
      <Text type="secondary">Monthly analytics for current training and internship programs</Text>
      <Card 
        style={{ marginTop: 24 }}
      >
        <Space>
          <RangePicker 
            value={dateRange}
            onChange={handleDateRangeChange}
            style={{ width: 300 }}
          />
          <Button
            icon={<ClearOutlined />} 
            onClick={handleClearRange}
            disabled={!dateRange}
          >
            Clear Range
          </Button>
          {[0, 1, 2].map((offset) => {
  const date = moment().subtract(offset, 'months');
  const monthName = date.format('MMMM');
  const year = date.format('YYYY');
  
  return (
    <Button 
      key={`${monthName}-${year}`} 
      onClick={() => setSessionMonth(offset)}
    >
      {monthName} {year}
    </Button>
  );
})}
        </Space>
      </Card>
      <Divider />
      
      {/* Overview Statistics */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={analytics.totalUsers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Users in Training"
              value={analytics.training}
              prefix={<BookOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Interviews Completed"
              value={analytics.interviewCompleted}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Users in Internship"
              value={analytics.usersStartedInternship}
              prefix={<FileDoneOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Divider />
      
      {/* Training Completion Section */}
      <Title level={3}>Training Progress</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Average Training Completion">
            <div style={{ textAlign: 'center' }}>
              <Progress 
                type="dashboard" 
                percent={analytics.avgTrainingCompletion} 
                format={percent => `${percent.toFixed(1)}%`}
              />
              <div style={{ marginTop: '20px' }}>
                <Text>Average completion rate across all users</Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Training Completion Distribution">
            <Table 
              dataSource={trainingCompletionData} 
              columns={trainingColumns} 
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Divider />
      
      {/* Interview Section */}
      <Title level={3}>Interview Performance</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Interview Completion Rate">
            <div style={{ textAlign: 'center' }}>
              <Progress 
                type="dashboard" 
                percent={(analytics.interviewCompleted / analytics.totalUsers) * 100} 
                format={percent => `${percent.toFixed(1)}%`}
              />
              <div style={{ marginTop: '20px' }}>
                <Text>{analytics.interviewCompleted} out of {analytics.totalUsers} users completed interviews</Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Interview Score Distribution">
            <Table 
              dataSource={interviewScoresData} 
              columns={interviewColumns} 
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Divider />
      
      {/* Internship Section */}
      <Title level={3}>Internship Analytics</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card>
            <Statistic
              title="Internship Started"
              value={analytics.internship}
              prefix={<CheckCircleOutlined />}
              suffix={`/ ${analytics.totalUsers}`}
            />
            <div style={{ marginTop: '16px' }}>
              <Progress 
                percent={(analytics.internship / analytics.totalUsers) * 100} 
                size="small" 
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card>
            <Statistic
              title="Success Stories"
              value={analytics.successStories}
              prefix={<TrophyOutlined />}
              suffix={`/ ${analytics.totalUsers}`}
            />
            <div style={{ marginTop: '16px' }}>
              <Progress 
                percent={(analytics.successStories / analytics.totalUsers) * 100} 
                size="small" 
              />
            </div>
          </Card>
        </Col>
      </Row>
      
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col span={24}>
          <Card title="Internship Task Submissions">
            <Table 
              dataSource={internshipTasksData} 
              columns={internshipColumns} 
              pagination={false}
            />
          </Card>
        </Col>
      </Row>

      <Divider />
      
      {/* Funnel Overview */}
      <Title level={3}>Program Funnel Overview</Title>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <div style={{ padding: '20px 0' }}>
              <Row align="middle" justify="space-around">
                <Col span={4} style={{ textAlign: 'center' }}>
                  <BarChartOutlined style={{ fontSize: '32px' }} />
                  <div>Total Users</div>
                  <Title level={4}>{analytics.totalUsers}</Title>
                </Col>
                <Col span={1} style={{ textAlign: 'center' }}>→</Col>
                <Col span={4} style={{ textAlign: 'center' }}>
                  <BookOutlined style={{ fontSize: '32px' }} />
                  <div>Training</div>
                  <Title level={4}>{analytics.training}</Title>
                  <Text type="secondary">({((analytics.training / analytics.totalUsers) * 100).toFixed(1)}%)</Text>
                </Col>
                <Col span={1} style={{ textAlign: 'center' }}>→</Col>
                <Col span={4} style={{ textAlign: 'center' }}>
                  <TeamOutlined style={{ fontSize: '32px' }} />
                  <div>Interviews</div>
                  <Title level={4}>{analytics.interviewCompleted}</Title>
                  <Text type="secondary">({((analytics.interviewCompleted / analytics.totalUsers) * 100).toFixed(1)}%)</Text>
                </Col>
                <Col span={1} style={{ textAlign: 'center' }}>→</Col>
                <Col span={4} style={{ textAlign: 'center' }}>
                  <FileDoneOutlined style={{ fontSize: '32px' }} />
                  <div>Internship</div>
                  <Title level={4}>{analytics.internship}</Title>
                  <Text type="secondary">({((analytics.internship / analytics.totalUsers) * 100).toFixed(1)}%)</Text>
                </Col>
                <Col span={1} style={{ textAlign: 'center' }}>→</Col>
                <Col span={4} style={{ textAlign: 'center' }}>
                  <TrophyOutlined style={{ fontSize: '32px' }} />
                  <div>Success</div>
                  <Title level={4}>{analytics.successStories}</Title>
                  <Text type="secondary">({((analytics.successStories / analytics.totalUsers) * 100).toFixed(1)}%)</Text>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AnalyticsDashboard;